import type { ReactNode } from "react";
import { LoadingScreen } from "@/shared/ui/feedback/Loading";
import { Message } from "@/shared/ui/feedback/Message";
import { Button } from "@/shared/ui/form/Button";

interface QueryStateBoundaryProps {
  children: ReactNode;
  error: unknown;
  hasData: boolean;
  isLoading: boolean;
  isRetrying?: boolean;
  message: string;
  onRetry?: () => void;
}

export const QueryStateBoundary: React.FunctionComponent<
  QueryStateBoundaryProps
> = ({
  children,
  error,
  hasData,
  isLoading,
  isRetrying = false,
  message,
  onRetry,
}: QueryStateBoundaryProps) => {
  const retryButton = onRetry ? (
    <Button onClick={onRetry} disabled={isRetrying}>
      {isRetrying ? "再試行中" : "再試行"}
    </Button>
  ) : null;

  // キャッシュ済みデータがある場合は、古い内容を表示したまま背景エラーを通知する。
  if (hasData) {
    return (
      <>
        {error && onRetry && (
          <aside
            className="mx-4 my-3 flex flex-wrap items-center justify-center gap-3 text-destructive"
            role="alert"
          >
            <p>{message}。表示中の内容を保持しています</p>
            {retryButton}
          </aside>
        )}
        {children}
      </>
    );
  }

  if (isLoading || isRetrying) return <LoadingScreen />;

  if (error) {
    return (
      <Message variant="error" className="flex-col gap-4">
        <p>{message}</p>
        {retryButton}
      </Message>
    );
  }

  const fallbackMessage = <p>データを読み込めませんでした</p>;

  return (
    <Message variant="warning" className="flex-col gap-4">
      {fallbackMessage}
      {retryButton}
    </Message>
  );
};
